<template>
  <div class="ac-overlay" @click.self="cancel">
    <div class="ac-modal">
      <div class="ac-header">
        <h6 class="ac-title">{{ step==='source' ? 'Choose photo source' : 'Crop your photo' }}</h6>
        <button class="btn btn-sm btn-outline-secondary" @click="cancel"><i class="mdi mdi-close"></i></button>
      </div>

      <!-- Step 1: pick source -->
      <div v-if="step==='source'" class="ac-source-grid">
        <button class="ac-source-btn" @click="$refs.fileInput.click()">
          <i class="mdi mdi-image-outline"></i>
          <div>Upload from device</div>
        </button>
        <button class="ac-source-btn" @click="openCamera('user')">
          <i class="mdi mdi-camera-account"></i>
          <div>Selfie / front camera</div>
        </button>
        <button class="ac-source-btn" @click="openCamera('environment')">
          <i class="mdi mdi-camera"></i>
          <div>Back camera</div>
        </button>
        <input ref="fileInput" type="file" accept="image/*" style="display:none;" @change="onFile" />
      </div>

      <!-- Step 2: live camera -->
      <div v-if="step==='camera'" class="ac-camera-wrap">
        <video ref="video" autoplay playsinline class="ac-video"></video>
        <div class="ac-cam-actions">
          <button class="btn btn-outline-light btn-sm" @click="step='source'; stopCamera()"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
          <button class="btn btn-warning fw-bold" @click="snap"><i class="mdi mdi-camera me-1"></i>Take photo</button>
        </div>
        <div v-if="cameraError" class="ac-error mt-2">{{ cameraError }}</div>
      </div>

      <!-- Step 3: crop -->
      <div v-if="step==='crop'">
        <div class="ac-crop-stage" ref="stage"
             @mousedown="onDragStart" @touchstart.prevent="onDragStart"
             @wheel.prevent="onWheel">
          <img v-if="imgSrc" :src="imgSrc" ref="img" class="ac-crop-img"
               :style="{transform:'translate(-50%,-50%) translate('+offsetX+'px,'+offsetY+'px) scale('+zoom+')'}" />
          <div class="ac-crop-frame"></div>
        </div>
        <div class="ac-zoom-row">
          <i class="mdi mdi-image-size-select-small"></i>
          <input type="range" min="1" max="3" step="0.05" v-model.number="zoom" class="form-range flex-fill" />
          <i class="mdi mdi-image-size-select-large"></i>
        </div>
        <div class="d-flex justify-content-between gap-2 mt-3">
          <button class="btn btn-outline-light btn-sm" @click="step='source';imgSrc='';"><i class="mdi mdi-restart me-1"></i>Pick again</button>
          <button class="btn btn-warning fw-bold" @click="confirmCrop" :disabled="busy">
            <span v-if="busy"><span class="spinner-border spinner-border-sm me-1"></span>Compressing…</span>
            <span v-else><i class="mdi mdi-check me-1"></i>Use this photo</span>
          </button>
        </div>
        <div class="ac-hint mt-2">Drag to reposition · scroll/slide to zoom · output is square 512×512 JPEG.</div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "AvatarCropper",
  emits: ['close', 'cropped'],
  props: {
    outputSize: { type: Number, default: 512 },
    quality: { type: Number, default: 0.85 }
  },
  data(){return{
    step:'source',          // 'source' | 'camera' | 'crop'
    imgSrc:'',
    imgEl:null,
    naturalW:0, naturalH:0,
    zoom:1, offsetX:0, offsetY:0,
    drag:null,
    cameraStream:null, cameraError:'',
    busy:false
  }},
  beforeUnmount(){ this.stopCamera(); document.removeEventListener('mousemove',this.onDragMove); document.removeEventListener('mouseup',this.onDragEnd); document.removeEventListener('touchmove',this.onDragMove); document.removeEventListener('touchend',this.onDragEnd); },
  methods:{
    cancel(){ this.stopCamera(); this.$emit('close'); },
    onFile(e){
      var f = e.target.files && e.target.files[0]; if (!f) return;
      if (!f.type.startsWith('image/')) { alert('Please pick an image file'); return; }
      var reader = new FileReader();
      reader.onload = ev => { this.loadIntoCrop(ev.target.result); };
      reader.readAsDataURL(f);
    },
    async openCamera(facing){
      this.cameraError = '';
      try {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
        this.step = 'camera';
        await this.$nextTick();
        if (this.$refs.video) this.$refs.video.srcObject = this.cameraStream;
      } catch(e) {
        this.cameraError = 'Could not open the camera (' + (e.name || 'permission denied') + '). Try Upload from device.';
        this.step = 'source';
      }
    },
    stopCamera(){
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(t => t.stop());
        this.cameraStream = null;
      }
    },
    snap(){
      var v = this.$refs.video; if (!v) return;
      var c = document.createElement('canvas');
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext('2d').drawImage(v, 0, 0);
      var dataUrl = c.toDataURL('image/jpeg', 0.92);
      this.stopCamera();
      this.loadIntoCrop(dataUrl);
    },
    loadIntoCrop(dataUrl){
      this.imgSrc = dataUrl;
      this.zoom = 1; this.offsetX = 0; this.offsetY = 0;
      var img = new Image();
      img.onload = () => {
        this.naturalW = img.naturalWidth;
        this.naturalH = img.naturalHeight;
        this.step = 'crop';
      };
      img.src = dataUrl;
    },
    onDragStart(e){
      var p = (e.touches && e.touches[0]) || e;
      this.drag = { x: p.clientX, y: p.clientY, ox: this.offsetX, oy: this.offsetY };
      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.onDragEnd);
      document.addEventListener('touchmove', this.onDragMove, { passive: false });
      document.addEventListener('touchend', this.onDragEnd);
    },
    onDragMove(e){
      if (!this.drag) return;
      if (e.cancelable) e.preventDefault();
      var p = (e.touches && e.touches[0]) || e;
      this.offsetX = this.drag.ox + (p.clientX - this.drag.x);
      this.offsetY = this.drag.oy + (p.clientY - this.drag.y);
    },
    onDragEnd(){
      this.drag = null;
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
      document.removeEventListener('touchmove', this.onDragMove);
      document.removeEventListener('touchend', this.onDragEnd);
    },
    onWheel(e){
      var delta = e.deltaY > 0 ? -0.08 : 0.08;
      this.zoom = Math.max(1, Math.min(3, this.zoom + delta));
    },
    confirmCrop(){
      this.busy = true;
      // The stage is square (CSS sets it). Compute the visible square in the source image.
      var stage = this.$refs.stage;
      var stageRect = stage.getBoundingClientRect();
      var stageSize = stageRect.width; // square
      // Image is positioned so its center sits at stage center plus (offsetX, offsetY), scaled by zoom.
      // Find what part of the image lies inside the square frame.
      var imgEl = this.$refs.img;
      var imgRect = imgEl.getBoundingClientRect();
      var displayedW = imgRect.width;
      var displayedH = imgRect.height;
      var srcPerDisplayed = this.naturalW / displayedW;
      // Frame on screen: centered in stage
      var frameLeft = stageRect.left;
      var frameTop = stageRect.top;
      // Pixel coords of frame relative to displayed image top-left
      var relLeft = frameLeft - imgRect.left;
      var relTop = frameTop - imgRect.top;
      // Convert to natural-image coordinates
      var sx = relLeft * srcPerDisplayed;
      var sy = relTop * srcPerDisplayed;
      var sSize = stageSize * srcPerDisplayed;
      // Clamp to bounds
      sx = Math.max(0, Math.min(this.naturalW - 1, sx));
      sy = Math.max(0, Math.min(this.naturalH - 1, sy));
      sSize = Math.max(1, Math.min(Math.min(this.naturalW - sx, this.naturalH - sy), sSize));

      var canvas = document.createElement('canvas');
      canvas.width = this.outputSize; canvas.height = this.outputSize;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000'; ctx.fillRect(0, 0, this.outputSize, this.outputSize);
      var sourceImg = new Image();
      sourceImg.onload = () => {
        ctx.drawImage(sourceImg, sx, sy, sSize, sSize, 0, 0, this.outputSize, this.outputSize);
        canvas.toBlob(blob => {
          var finalize = (b) => {
            var name = 'avatar-' + Date.now() + '.jpg';
            var file = new File([b], name, { type: 'image/jpeg' });
            this.$emit('cropped', { file: file, dataUrl: canvas.toDataURL('image/jpeg', this.quality) });
            this.busy = false;
            this.$emit('close');
          };
          if (blob && blob.size > 350 * 1024) {
            // Drop quality to ensure we stay light
            canvas.toBlob(b2 => finalize(b2 || blob), 'image/jpeg', 0.65);
          } else {
            finalize(blob);
          }
        }, 'image/jpeg', this.quality);
      };
      sourceImg.src = this.imgSrc;
    }
  }
};
</script>

<style scoped>
.ac-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;}
.ac-modal{width:100%;max-width:480px;background:#141414;border:1px solid #1E1E1E;border-radius:14px;padding:18px;color:#F5F0E8;}
html[data-theme="light"] .ac-modal{background:#FFFFFF;border-color:#E8E2DA;color:#1A1A1A}
.ac-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
.ac-title{margin:0;font-size:15px;font-weight:700}
.ac-source-grid{display:grid;grid-template-columns:1fr;gap:10px}
@media(min-width:540px){.ac-source-grid{grid-template-columns:1fr 1fr}}
.ac-source-btn{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;border-radius:12px;padding:24px 12px;font-size:13px;cursor:pointer;transition:all .15s;}
.ac-source-btn:hover{border-color:#BFA164;background:rgba(191,161,100,.05)}
.ac-source-btn i{font-size:32px;color:#BFA164}
html[data-theme="light"] .ac-source-btn{background:#F7F5F2;border-color:#E0D8D0;color:#1A1A1A}
html[data-theme="light"] .ac-source-btn:hover{background:#FFF7E6;border-color:#BFA164}
.ac-camera-wrap{display:flex;flex-direction:column;align-items:center;gap:10px}
.ac-video{width:100%;max-width:420px;aspect-ratio:1/1;object-fit:cover;background:#000;border-radius:12px}
.ac-cam-actions{display:flex;gap:10px;justify-content:center;width:100%}
.ac-crop-stage{position:relative;width:100%;aspect-ratio:1/1;background:#000;border-radius:12px;overflow:hidden;cursor:grab;user-select:none;touch-action:none}
.ac-crop-stage:active{cursor:grabbing}
.ac-crop-img{position:absolute;top:50%;left:50%;max-width:none;width:100%;height:auto;transform-origin:50% 50%;pointer-events:none}
.ac-crop-frame{position:absolute;inset:0;border:2px dashed rgba(191,161,100,.6);border-radius:50%;pointer-events:none;box-shadow:0 0 0 9999px rgba(0,0,0,.45)}
.ac-zoom-row{display:flex;align-items:center;gap:10px;margin-top:14px;color:#A09888}
.ac-hint{color:#6B6356;font-size:11px;text-align:center}
.ac-error{color:#FF6B6B;font-size:12px;text-align:center}
</style>
